import React, { useEffect, useState } from "react";
import { sortBy } from "lodash";

import { Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { HelpText, SpendBrandCard, CategoryCard } from "@/components/atoms";
import { useInsightFilters } from "@/context";
import { InsightResult } from "@/types";
import { filterBrands, filterCategories } from "@/utils";

interface SpendViewProps {
  categories: InsightResult[];
  brands: InsightResult[];
}

export const SpendView: React.FC<SpendViewProps> = ({ categories, brands }) => {
  const {
    filters: { scoreType, levels, searchText },
  } = useInsightFilters();

  const [selectedPrimary, setSelectedPrimary] = useState<string | undefined>(
    undefined,
  );
  const [selectedSecondary, setSelectedSecondary] = useState<
    string | undefined
  >(undefined);

  const [secondaryCategories, setSecondaryCategories] = useState<
    InsightResult[]
  >([]);
  const [subsetBrands, setSubsetBrands] = useState<InsightResult[]>([]);

  const primaryCategories = sortBy(
    categories.filter((x) => x.tag_name.includes("All")),
    scoreType.value,
  )
    .reverse()
    .map((x) => x.tag_category);

  const maxPrimaryScore =
    scoreType.value === "rate"
      ? 1
      : Math.max.apply(
          Math,
          categories
            .filter((x) => x.tag_name.includes("All"))
            .map((x) => Number(x[scoreType.value as keyof InsightResult])),
        );
  const minPrimaryScore =
    scoreType.value === "rate"
      ? Math.min.apply(
          Math,
          categories
            .filter((x) => x.tag_name.includes("All"))
            .map((x) => Number(x[scoreType.value as keyof InsightResult])),
        )
      : Math.max(1 - (maxPrimaryScore - 1), 0);

  const maxSecondaryScore =
    scoreType.value === "rate"
      ? 1
      : Math.max.apply(
          Math,
          categories.map((x) =>
            Number(x[scoreType.value as keyof InsightResult]),
          ),
        );
  const minSecondaryScore =
    scoreType.value === "rate"
      ? Math.min.apply(
          Math,
          categories.map((x) =>
            Number(x[scoreType.value as keyof InsightResult]),
          ),
        )
      : Math.max(1 - (maxSecondaryScore - 1), 0);

  const maxBrandScore =
    scoreType.value === "rate"
      ? 1
      : Math.min(
          Math.max.apply(
            Math,
            brands.map((x) =>
              Number(x[scoreType.value as keyof InsightResult]),
            ),
          ),
          1.5,
        );
  const minBrandScore =
    scoreType.value === "rate"
      ? Math.min.apply(
          Math,
          brands.map((x) => Number(x[scoreType.value as keyof InsightResult])),
        )
      : Math.max(1 - (maxBrandScore - 1), 0);

  // Clear the category selection if a user searches - it's a global search
  useEffect(() => {
    if (searchText !== "") {
      setSelectedPrimary(undefined);
      setSelectedSecondary(undefined);
    }
  }, [searchText]);

  // When a primary category is selected, filter the secondary categories
  useEffect(() => {
    if (selectedPrimary) {
      setSecondaryCategories(
        filterCategories(
          categories,
          selectedPrimary,
          selectedPrimary,
          scoreType.value,
        ),
      );
    } else {
      setSecondaryCategories([]);
    }
  }, [categories, selectedPrimary, scoreType]);

  // Filter the brands using the category selection and other filters
  useEffect(() => {
    setSubsetBrands(
      filterBrands(
        brands,
        selectedPrimary,
        selectedSecondary,
        levels,
        searchText,
        scoreType.value,
        !selectedPrimary && !selectedSecondary ? 20 : undefined,
      ),
    );
  }, [
    brands,
    selectedPrimary,
    selectedSecondary,
    levels,
    searchText,
    scoreType,
  ]);

  const handlePrimarySelect = (category: string) => {
    setSelectedPrimary((prevState) =>
      prevState !== category ? category : undefined,
    );
    setSelectedSecondary(undefined);
  };

  const handleSecondarySelect = (category: string) => {
    setSelectedSecondary((prevState) =>
      prevState !== category ? category : undefined,
    );
  };

  if (searchText !== "") {
    return (
      <>
        <Grid size={12}>
          <Typography>Search Results</Typography>
        </Grid>
        <Grid container size={12} spacing={2}>
          {subsetBrands.map((brand, index) => (
            <SpendBrandCard
              key={`spend-brand-${index}`}
              brand={brand.tag_name}
              value={Number(brand[scoreType.value as keyof InsightResult])}
              score={scoreType.value}
              minValue={minBrandScore}
              maxValue={maxBrandScore}
            />
          ))}
        </Grid>
      </>
    );
  } else {
    return (
      <>
        {/* Primary Category View */}
        <Grid size={12}>
          <HelpText
            text="Primary Category"
            helpText="Select a Primary Category to show it's Secondary Categories and filter the Brands below"
          />
        </Grid>
        <Grid container size={12} spacing={2}>
          {primaryCategories.map((category, index) => {
            const categoryScore = categories
              .filter((x) => x.tag_category === category)
              .find((x) => x.tag_name.includes("All "));
            if (categoryScore) {
              return (
                <CategoryCard
                  key={`spend-category-${index}`}
                  category={categoryScore.tag_name}
                  value={Number(
                    categoryScore[scoreType.value as keyof InsightResult],
                  )}
                  score={scoreType.value}
                  type="spend"
                  minValue={minPrimaryScore}
                  maxValue={maxPrimaryScore}
                  selectedCategory={selectedPrimary}
                  onSelect={handlePrimarySelect}
                />
              );
            } else {
              return null;
            }
          })}

          {/* Secondary Category View */}
          {selectedPrimary && (
            <>
              <Grid size={12}>
                <HelpText
                  text="Secondary Category"
                  helpText="Select a Secondary Category to filter the Brands below"
                />
              </Grid>
              {secondaryCategories.map((category, index) => (
                <CategoryCard
                  key={`spend-category-${index}`}
                  category={category.tag_name}
                  value={Number(
                    category[scoreType.value as keyof InsightResult],
                  )}
                  score={scoreType.value}
                  type="spend"
                  minValue={minSecondaryScore}
                  maxValue={maxSecondaryScore}
                  selectedCategory={selectedSecondary}
                  onSelect={handleSecondarySelect}
                />
              ))}
            </>
          )}

          {/* Brand View */}
          <Grid size={12}>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
              <Typography>
                {selectedSecondary
                  ? selectedSecondary
                  : selectedPrimary
                    ? selectedPrimary
                    : "Top Scoring"}
              </Typography>
              <Typography>Brands</Typography>
            </Stack>
          </Grid>
          {subsetBrands.map((brand, index) => (
            <SpendBrandCard
              key={`spend-brand-${index}`}
              brand={brand.tag_name}
              value={Number(brand[scoreType.value as keyof InsightResult])}
              score={scoreType.value}
              minValue={minBrandScore}
              maxValue={maxBrandScore}
            />
          ))}
        </Grid>
      </>
    );
  }
};

export default SpendView;
